import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Flame, Leaf, Star } from "lucide-react";

// Signature dishes with imagery — matched to real menu items by name.
// These are the "hero" plates that give the menu a visual anchor.
const SIGNATURES = [
  {
    name: "Margherita Pizza",
    image:
      "https://images.unsplash.com/photo-1604068549290-dea0e4a305ca?auto=format&fit=crop&w=900&q=80",
    tag: "Signature",
  },
  {
    name: "Emilio's Special Pizza",
    image:
      "https://images.unsplash.com/photo-1571407970349-bc81e7e96d47?auto=format&fit=crop&w=900&q=80",
    tag: "House favorite",
  },
  {
    name: "Sicilian Pizza",
    image:
      "https://images.unsplash.com/photo-1594007654729-407eedc4be65?auto=format&fit=crop&w=900&q=80",
    tag: "Classic",
  },
  {
    name: "Penne Vodka",
    image:
      "https://images.pexels.com/photos/1049626/pexels-photo-1049626.jpeg?auto=compress&cs=tinysrgb&w=900",
    tag: "Best-seller",
  },
];

// Loose keyword hints for auto-tagging items
const POPULAR_HINTS = [
  "margherita",
  "emilio",
  "meat lovers",
  "sicilian",
  "penne vodka",
  "chicken parmigiana",
  "grandma",
  "buffalo",
];
const VEG_HINTS = [
  "margherita",
  "cheese pizza",
  "vegetable",
  "eggplant",
  "veggie",
  "ricotta",
  "spinach",
  "gluten free",
  "salad",
  "caesar",
  "bruschetta",
  "grilled vegetable",
];
const SPICY_HINTS = ["buffalo", "spicy", "hot"];

const hasHint = (name = "", hints) => {
  const n = name.toLowerCase();
  return hints.some((h) => n.includes(h));
};

export default function MenuSection({ categories = [], items = [], settings }) {
  const orderUrl = settings?.order_url || "#";

  const sortedCategories = useMemo(
    () => categories.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [categories]
  );

  const [activeCat, setActiveCat] = useState("__all__");

  const visibleItems = useMemo(() => {
    const src =
      activeCat === "__all__"
        ? items
        : items.filter((i) => i.category_id === activeCat);
    return src.slice().sort((a, b) => {
      const ca = categories.find((c) => c.id === a.category_id)?.order ?? 0;
      const cb = categories.find((c) => c.id === b.category_id)?.order ?? 0;
      if (ca !== cb) return ca - cb;
      return (a.order ?? 0) - (b.order ?? 0);
    });
  }, [items, activeCat, categories]);

  // Group visible items by category for nicer rendering
  const groupedVisible = useMemo(() => {
    const map = new Map();
    for (const it of visibleItems) {
      const cat = categories.find((c) => c.id === it.category_id);
      if (!cat) continue;
      if (!map.has(cat.id)) map.set(cat.id, { category: cat, items: [] });
      map.get(cat.id).items.push(it);
    }
    return Array.from(map.values());
  }, [visibleItems, categories]);

  // Signature dishes actually present in the menu
  const signatures = useMemo(() => {
    return SIGNATURES.map((s) => {
      const found = items.find(
        (it) => it.name.toLowerCase() === s.name.toLowerCase()
      );
      return found ? { ...s, item: found } : null;
    }).filter(Boolean);
  }, [items]);

  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="relative py-24 md:py-32 bg-[var(--dough)] overflow-hidden"
    >
      {/* Faint decorative pattern */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #1A1713 1px, transparent 0)",
          backgroundSize: "22px 22px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <span className="section-rule" />
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[var(--charcoal)] font-semibold leading-[1.05]">
              The Menu
            </h2>
            <p className="mt-4 text-[var(--charcoal-soft)] max-w-xl text-base md:text-lg">
              Handmade dough, homemade sauces, ingredients we&apos;d serve to
              our own family. Browse below or order the full list on Slice.
            </p>
          </div>
          <a
            href={orderUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="menu-order-btn"
            className="inline-flex items-center gap-2 self-start md:self-end px-6 py-3 rounded-full border border-[var(--charcoal)]/20 hover:border-[var(--terracotta)] hover:text-[var(--terracotta)] transition-colors font-medium"
          >
            View full menu <ArrowUpRight size={16} />
          </a>
        </div>

        {/* Signature dishes */}
        {signatures.length > 0 && (
          <div
            data-testid="menu-signatures"
            className="mb-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
          >
            {signatures.map((s) => (
              <a
                key={s.name}
                href={orderUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative rounded-2xl overflow-hidden bg-[var(--charcoal)] aspect-[4/5] block"
                data-testid={`signature-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <img
                  src={s.image}
                  alt={s.name}
                  loading="lazy"
                  className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                <div className="absolute top-3 left-3">
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] bg-white/90 text-[var(--charcoal)] px-2.5 py-1 rounded-full font-medium">
                    <Star size={10} className="text-[var(--terracotta)]" />
                    {s.tag}
                  </span>
                </div>
                <div className="absolute bottom-0 inset-x-0 p-4 text-white">
                  <div className="font-serif text-lg leading-tight">
                    {s.name}
                  </div>
                  <div className="mt-1 flex items-center justify-between">
                    <span className="text-sm opacity-85 line-clamp-1">
                      {s.item.description || "Baked fresh daily"}
                    </span>
                    <span className="font-semibold text-[#F5D7A6]">
                      {s.item.price}
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}

        {/* Category tabs */}
        <div className="sticky top-16 md:top-20 z-30 -mx-5 md:mx-0 mb-8 backdrop-blur-md bg-[var(--dough)]/85">
          <div
            className="px-5 md:px-0 py-3 flex gap-2 overflow-x-auto no-scrollbar border-b border-[var(--line)]"
            data-testid="menu-tabs"
          >
            <TabBtn
              active={activeCat === "__all__"}
              onClick={() => setActiveCat("__all__")}
              testid="menu-tab-all"
            >
              All
            </TabBtn>
            {sortedCategories.map((c) => (
              <TabBtn
                key={c.id}
                active={activeCat === c.id}
                onClick={() => setActiveCat(c.id)}
                testid={`menu-tab-${c.id}`}
              >
                {c.name}
              </TabBtn>
            ))}
          </div>
        </div>

        {/* Grouped item list */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCat}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="space-y-8"
            data-testid="menu-list"
          >
            {groupedVisible.map(({ category, items }, catIdx) => (
              <CategoryCard
                key={category.id}
                category={category}
                items={items}
                index={catIdx}
              />
            ))}
          </motion.div>
        </AnimatePresence>

        {/* CTA */}
        <div className="mt-16 text-center">
          <p className="text-sm text-[var(--charcoal-soft)] mb-4">
            Prices &amp; availability may vary. See full menu on Slice.
          </p>
          <a
            href={orderUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="menu-cta-order"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[var(--terracotta)] hover:bg-[var(--terracotta-hover)] text-white font-semibold transition-all hover:-translate-y-0.5 shadow-md shadow-[var(--terracotta)]/20"
          >
            Order online <ArrowUpRight size={18} />
          </a>
        </div>
      </div>

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </section>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
        active
          ? "bg-[var(--charcoal)] text-white border-[var(--charcoal)]"
          : "bg-transparent text-[var(--charcoal)] border-[var(--line)] hover:border-[var(--terracotta)] hover:text-[var(--terracotta)]"
      }`}
    >
      {children}
    </button>
  );
}

// Palette rotation for category cards — each card feels distinct but harmonised
const CARD_PALETTES = [
  {
    // 1: Dark charcoal — dramatic
    bg: "#1A1713",
    text: "#F9F6F0",
    subtext: "rgba(249,246,240,0.65)",
    price: "#F5D7A6",
    accent: "#F5D7A6",
    leader: "rgba(249,246,240,0.25)",
    border: "rgba(245,215,166,0.15)",
    itemHover: "#F5D7A6",
  },
  {
    // 2: Terracotta warm — appetite
    bg: "#F4E3D5",
    text: "#1A1713",
    subtext: "#6B5A48",
    price: "#A83C27",
    accent: "#A83C27",
    leader: "rgba(26,23,19,0.25)",
    border: "rgba(168,60,39,0.25)",
    itemHover: "#A83C27",
  },
  {
    // 3: Ivory book — classic
    bg: "#EFEADD",
    text: "#1A1713",
    subtext: "#5C564D",
    price: "#A83C27",
    accent: "#3B4D36",
    leader: "rgba(26,23,19,0.2)",
    border: "rgba(59,77,54,0.2)",
    itemHover: "#3B4D36",
  },
  {
    // 4: Olive-tinted — herby
    bg: "#DEE3D2",
    text: "#1A1713",
    subtext: "#5C564D",
    price: "#3B4D36",
    accent: "#3B4D36",
    leader: "rgba(26,23,19,0.22)",
    border: "rgba(59,77,54,0.28)",
    itemHover: "#A83C27",
  },
];

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];

function CategoryCard({ category, items, index }) {
  const palette = CARD_PALETTES[index % CARD_PALETTES.length];
  const isDark = palette.bg === "#1A1713";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      data-testid={`category-card-${category.id}`}
      className="rounded-3xl overflow-hidden border relative"
      style={{
        backgroundColor: palette.bg,
        color: palette.text,
        borderColor: palette.border,
      }}
    >
      {/* Decorative corner ornament */}
      <div
        aria-hidden
        className="absolute top-6 right-6 font-serif italic opacity-30 select-none text-6xl leading-none"
        style={{ color: palette.accent }}
      >
        ❦
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-0">
        {/* Category header pane */}
        <div
          className="p-8 lg:p-10 lg:border-r"
          style={{ borderColor: palette.border }}
        >
          <div
            className="text-xs font-medium tracking-[0.3em] uppercase mb-4"
            style={{ color: palette.accent }}
          >
            Chapter {ROMAN[index] || index + 1}
          </div>
          <h3
            className="font-serif text-3xl md:text-4xl font-semibold leading-[1.05]"
            style={{ color: palette.text }}
          >
            {category.name}
          </h3>
          {category.description && (
            <p
              className="mt-4 text-sm italic leading-relaxed"
              style={{ color: palette.subtext }}
            >
              {category.description}
            </p>
          )}
          <div
            className="mt-6 h-px w-16"
            style={{ backgroundColor: palette.accent }}
          />
          <div
            className="mt-3 text-xs uppercase tracking-widest"
            style={{ color: palette.subtext }}
          >
            {items.length} {items.length === 1 ? "dish" : "dishes"}
          </div>
        </div>

        {/* Items grid */}
        <div className="p-6 md:p-8 lg:p-10">
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-1">
            {items.map((it, idx) => (
              <PaletteMenuRow
                key={it.id}
                item={it}
                index={idx}
                palette={palette}
                isDark={isDark}
              />
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
}

function PaletteMenuRow({ item, index, palette, isDark }) {
  const popular = hasHint(item.name, POPULAR_HINTS);
  const veggie = hasHint(item.name, VEG_HINTS);
  const spicy = hasHint(item.name, SPICY_HINTS);

  return (
    <motion.li
      initial={{ opacity: 0, y: 6 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.3, delay: Math.min(index * 0.02, 0.25) }}
      className="group py-3 border-b border-dashed"
      style={{ borderColor: palette.border }}
      data-testid={`menu-row-${item.id}`}
    >
      <div className="flex items-baseline gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="font-serif text-lg md:text-xl font-medium transition-colors"
              style={{ color: palette.text }}
              onMouseEnter={(e) => (e.currentTarget.style.color = palette.itemHover)}
              onMouseLeave={(e) => (e.currentTarget.style.color = palette.text)}
            >
              {item.name}
            </span>
            {popular && (
              <PaletteBadge palette={palette} isDark={isDark} testid="badge-popular">
                <Star size={10} /> Popular
              </PaletteBadge>
            )}
            {spicy && (
              <PaletteBadge palette={palette} isDark={isDark} testid="badge-spicy">
                <Flame size={10} /> Spicy
              </PaletteBadge>
            )}
            {veggie && !spicy && (
              <PaletteBadge
                palette={palette}
                isDark={isDark}
                testid="badge-veggie"
                tone="veggie"
              >
                <Leaf size={10} /> Veggie
              </PaletteBadge>
            )}
          </div>
          {item.description && (
            <div
              className="text-sm mt-1 leading-relaxed"
              style={{ color: palette.subtext }}
            >
              {item.description}
            </div>
          )}
        </div>
        <div
          aria-hidden
          className="flex-1 min-w-6 border-b border-dotted mb-1.5 hidden sm:block"
          style={{ borderColor: palette.leader }}
        />
        <div
          className="font-serif font-semibold text-lg whitespace-nowrap"
          style={{ color: palette.price }}
        >
          {item.price}
        </div>
      </div>
    </motion.li>
  );
}

function PaletteBadge({ children, palette, isDark, tone = "accent", testid }) {
  const isVeg = tone === "veggie";
  const bg = isDark
    ? "rgba(245,215,166,0.15)"
    : isVeg
    ? "rgba(59,77,54,0.12)"
    : "rgba(168,60,39,0.12)";
  const color = isDark ? palette.accent : isVeg ? "#3B4D36" : "#A83C27";
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full font-semibold"
      style={{ backgroundColor: bg, color }}
    >
      {children}
    </span>
  );
}
